﻿import { Box, LinearProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  CircleSVG,
  RectangleSVG,
  CircleSVGWhite,
  RectangleSVGWhite,
} from "@/ImageRepo";

import "./LoadingBar.css";

export const LoadingAnimation = ({ size } = {}) => {
  const scale = size != null ? size / 80 : 1;
  const sizeStyle =
    size != null
      ? {
        width: size,
        height: size,
        transform: `scale(${scale})`,
        transformOrigin: "center center",
      }
      : {};

  return (
    <Box
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...(size != null ? { marginBottom: 0 } : { marginBottom: "30px" }),
        ...sizeStyle,
      }}
      className={
        size != null ? "svg-container svg-container--inline" : "svg-container"
      }
    >
      <RectangleSVG className="rectangle" />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "center",
        }}
        className="circle-containers"
      >
        <CircleSVG className="circle-1" />
        <CircleSVG className="circle-2" />
        <CircleSVG className="circle-3" />
      </div>
    </Box>
  );
};

export const DarkLoadingAnimation = ({ size } = {}) => {
  const scale = size != null ? size / 80 : 1;
  const sizeStyle =
    size != null
      ? {
        width: size,
        height: size,
        transform: `scale(${scale})`,
        transformOrigin: "center center",
      }
      : {};

  return (
    <Box
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...(size != null ? { marginBottom: 0 } : { marginBottom: "30px" }),
        ...sizeStyle,
      }}
      className={
        size != null ? "svg-container svg-container--inline" : "svg-container"
      }
    >
      <RectangleSVGWhite className="rectangle" />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "center",
        }}
        className="circle-containers"
      >
        <CircleSVGWhite className="circle-1" />
        <CircleSVGWhite className="circle-2" />
        <CircleSVGWhite className="circle-3" />
      </div>
    </Box>
  );
};

const LoadingBar = ({
  isDataLoaded,
  setFinishedAnimation,
  secondsToStall = 10,
  isDarkMode = false,
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let timer;
    if (isDataLoaded) {
      //80%-100%
      const remainderProgress = 100 - progress;
      clearInterval(timer);
      timer = setInterval(() => {
        setProgress((oldProgress) => {
          if (oldProgress === 100) {
            return 100;
          }
          let diff = remainderProgress / 2;
          return Math.min(oldProgress + diff, 100);
        });
      }, 250);
    } else {
      //0%-80%
      timer = setInterval(() => {
        if (progress < 80) {
          setProgress((oldProgress) => {
            if (oldProgress === 80) {
              return 80;
            }
            let diff = (80 - oldProgress) / (2 * secondsToStall); // x/2 = roughly 1s or 4 loops
            diff = Math.max(diff, 1);
            return Math.min(oldProgress + diff, 80);
          });
        }
      }, 250);
    }

    return () => {
      clearInterval(timer);
    };
  }, [isDataLoaded]);

  useEffect(() => {
    let timeout;
    if (progress === 100) {
      timeout = setTimeout(() => {
        //allow bar to animate the last progress in the bar
        setFinishedAnimation(true);
      }, 250);
    }
    return () => clearTimeout(timeout);
  }, [progress]);

  return (
    <Box
      sx={{
        width: "25%",
        marginLeft: "auto",
        marginRight: "auto",
        textAlign: "center",
      }}
      className="progress-bar-container"
    >
      {isDarkMode ? <DarkLoadingAnimation /> : <LoadingAnimation />}

      <LinearProgress
        variant="determinate"
        value={progress}
        className="loading-bar"
      />

      <Typography
        variant="subheading"
        sx={
          isDarkMode
            ? { color: "var(--default-light)" }
            : { color: "var(--default-dark)" }
        }
      >
        Loading
      </Typography>
    </Box>
  );
};

export default LoadingBar;
